import React from 'react';

import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  Stack,
  Typography,
} from '@mui/material';

import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined';
import PublishOutlinedIcon from '@mui/icons-material/PublishOutlined';

import { IBieLoadSessionPublishResult, IBieOnboardingWorkbench } from './bieonboardingworkbench.models';

export interface IBieOnboardingPublishProps {
  workbench: IBieOnboardingWorkbench | null;
  publishing?: boolean;
  result?: IBieLoadSessionPublishResult | null;
}

const formatDate = (value?: string | null): string => {
  if (!value) {
    return '—';
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString();
};

const formatCoverage = (start?: string | null, end?: string | null): string => {
  if (!start && !end) {
    return 'Not specified';
  }

  return `${start || '—'} to ${end || '—'}`;
};

export default class BieOnboardingPublish extends React.PureComponent<IBieOnboardingPublishProps> {
  public render(): React.ReactNode {
    const { workbench, publishing = false, result = null } = this.props;

    if (!workbench) {
      return null;
    }

    const { loadSession, summary, actions } = workbench;
    const published = String(loadSession.stateCode).toUpperCase() === 'PUBLISHED';
    const blocking = Number(summary?.issues?.blocking || 0);
    const warnings = Number(summary?.issues?.warning || 0);
    const ready = Boolean(actions.canPublish) && blocking === 0;

    return (
      <Stack spacing={2}>
        <Alert
          severity={published ? 'success' : ready ? 'info' : 'warning'}
          icon={published ? <VerifiedOutlinedIcon /> : <PublishOutlinedIcon />}
        >
          {published
            ? 'This onboarding Load Session has been authoritatively published and is ready for Planning Cycle.'
            : ready
              ? 'This onboarding Load Session has passed validation and is ready for authoritative publication.'
              : 'Publication remains unavailable until validation is complete and all blocking issues are cleared.'}
        </Alert>

        <Grid container spacing={2}>
          <Grid size={{ xs:12, md:7 }}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent>
                <Stack spacing={2}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                    <Box>
                      <Typography variant="overline" color="text.secondary">
                        Authoritative Publication
                      </Typography>
                      <Typography variant="h6" fontWeight={700}>
                        {loadSession.code} · {loadSession.name}
                      </Typography>
                    </Box>
                    <Chip
                      label={published ? 'Published' : loadSession.stateName || loadSession.stateCode}
                      color={published ? 'success' : ready ? 'primary' : 'default'}
                      variant={published ? 'filled' : 'outlined'}
                    />
                  </Stack>

                  <Divider />

                  <Grid container spacing={2}>
                    <Grid size={{ xs:12, sm:6 }}>
                      <Typography variant="caption" color="text.secondary">Scope</Typography>
                      <Typography fontWeight={600}>{loadSession.scopeName || loadSession.scopeCode}</Typography>
                    </Grid>
                    <Grid size={{ xs:12, sm:6 }}>
                      <Typography variant="caption" color="text.secondary">Source organization</Typography>
                      <Typography fontWeight={600}>{loadSession.sourceOrganization || '—'}</Typography>
                    </Grid>
                    <Grid size={{ xs:12, sm:6 }}>
                      <Typography variant="caption" color="text.secondary">Coverage</Typography>
                      <Typography fontWeight={600}>
                        {formatCoverage(loadSession.coverageStartDate, loadSession.coverageEndDate)}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs:12, sm:6 }}>
                      <Typography variant="caption" color="text.secondary">Historical cutoff</Typography>
                      <Typography fontWeight={600}>{loadSession.historicalCutoffDate || '—'}</Typography>
                    </Grid>
                    <Grid size={{ xs:12, sm:4 }}>
                      <Typography variant="caption" color="text.secondary">Blocking issues</Typography>
                      <Typography fontWeight={700}>{blocking.toLocaleString()}</Typography>
                    </Grid>
                    <Grid size={{ xs:12, sm:4 }}>
                      <Typography variant="caption" color="text.secondary">Warnings</Typography>
                      <Typography fontWeight={700}>{warnings.toLocaleString()}</Typography>
                    </Grid>
                    <Grid size={{ xs:12, sm:4 }}>
                      <Typography variant="caption" color="text.secondary">Published at</Typography>
                      <Typography fontWeight={600}>{formatDate(loadSession.publishedAt || result?.publishedAt)}</Typography>
                    </Grid>
                  </Grid>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid size={{ xs:12, md:5 }}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent>
                <Stack spacing={1.5}>
                  <Typography variant="overline" color="text.secondary">
                    Governance
                  </Typography>
                  <Typography variant="h6" fontWeight={700}>
                    Certification checkpoint
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Publishing certifies that this Load Session has completed staging and validation and may be used as an authoritative historical source for downstream planning.
                  </Typography>
                  <Divider />
                  <Typography variant="body2">
                    The primary Workbench action will change automatically from <strong>Publish</strong> to <strong>Exit to Planning Cycle</strong> after a successful API call and Workbench refresh.
                  </Typography>
                  {publishing && (
                    <Alert severity="info">Publishing the Load Session…</Alert>
                  )}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Stack>
    );
  }
}
